import uniqueId from 'lodash/uniqueId'
import type {
  CompanyRuleNode,
  CompanyRulePartCapability,
  CompanyRuleTriggerCapability,
  SalaryCodeNode,
} from '@/modules/companies/types.graphql'
import {
  CompanyRuleConditionType,
  CompanyRuleEffectType,
  CompanyRuleTrigger,
} from '@/modules/companies/types.graphql'

export type RulePartValue = string | number | null

export interface CompanyRulePartForm {
  id: string
  type: string
  values: Record<string, RulePartValue>
  labels: Record<string, string>
}

export interface CompanyRuleForm {
  id: string
  name: string
  active: boolean
  trigger: CompanyRulePartForm | null
  conditions: CompanyRulePartForm[]
  effects: CompanyRulePartForm[]
}

export interface RuleTranslations {
  triggerManual: string
  conditionSalaryWorkTime: string
  conditionSalaryCode: string
  effectAddSalaryCodeAmount: string
  salaryWorkTime: string
  salaryCode: string
  amount: string
}

/**
 * Default capabilities used as a fallback while the backend capability set has
 * not loaded yet. New triggers/conditions/effects are expected to be added on
 * the backend and surfaced through `ruleCapabilities`, so the UI stays generic.
 */
export const DEFAULT_RULE_CAPABILITIES: CompanyRuleTriggerCapability[] = [
  {
    trigger: CompanyRuleTrigger.MANUAL_TIME_ENTRY_SALARY_CALCULATION,
    conditions: [
      {
        type: CompanyRuleConditionType.SALARY_WORK_TIME_EQUALS,
        fields: [
          { key: 'salaryWorkTime', valueType: 'SALARY_WORK_TIME', required: true },
        ],
      },
      {
        type: CompanyRuleConditionType.SALARY_CODE_EQUALS,
        fields: [{ key: 'salaryCode', valueType: 'SALARY_CODE', required: true }],
      },
    ],
    effects: [
      {
        type: CompanyRuleEffectType.ADD_SALARY_CODE_AMOUNT,
        fields: [
          { key: 'salaryCode', valueType: 'SALARY_CODE', required: true },
          { key: 'amount', valueType: 'POSITIVE_DECIMAL', required: true },
        ],
      },
    ],
  },
]

export function formatSalaryCodeName(
  salaryCode?: Pick<SalaryCodeNode, 'name' | 'externalId'> | null
): string {
  if (!salaryCode) return ''
  return salaryCode.externalId
    ? `(${salaryCode.externalId}) ${salaryCode.name}`
    : salaryCode.name
}

function makeDefaultValues(
  capability?: CompanyRulePartCapability
): Record<string, RulePartValue> {
  return (
    capability?.fields.reduce<Record<string, RulePartValue>>((acc, field) => {
      acc[field.key] = field.valueType === 'POSITIVE_DECIMAL' ? 3 : null
      return acc
    }, {}) ?? {}
  )
}

export function makePart(
  type: string,
  capability?: CompanyRulePartCapability
): CompanyRulePartForm {
  return {
    id: uniqueId('company-rule-part-'),
    type,
    values: makeDefaultValues(capability),
    labels: {},
  }
}

export function makeTriggerPart(trigger: string): CompanyRulePartForm {
  return {
    id: uniqueId('company-rule-trigger-'),
    type: trigger,
    values: {},
    labels: {},
  }
}

export function isValueValid(
  value: RulePartValue | undefined,
  valueType: string
): boolean {
  if (value === null || typeof value === 'undefined' || value === '')
    return false
  if (valueType === 'POSITIVE_DECIMAL') return Number(value) > 0
  return true
}

export function isPartValid(
  part: CompanyRulePartForm,
  capabilities: CompanyRulePartCapability[]
): boolean {
  const capability = capabilities.find(option => option.type === part.type)
  if (!capability) return false
  return capability.fields.every(
    field =>
      !field.required || isValueValid(part.values[field.key], field.valueType)
  )
}

export function getFirstAvailableCapability(
  capabilities: CompanyRulePartCapability[],
  existing: CompanyRulePartForm[]
) {
  return (
    capabilities.find(
      capability => !existing.some(part => part.type === capability.type)
    ) ?? capabilities[0]
  )
}

export function getRulePartDisplayName(
  type: string,
  translations: RuleTranslations
): string {
  switch (type) {
    case 'MANUAL_TIME_ENTRY_SALARY_CALCULATION':
      return translations.triggerManual
    case 'SALARY_WORK_TIME_EQUALS':
      return translations.conditionSalaryWorkTime
    case 'SALARY_CODE_EQUALS':
      return translations.conditionSalaryCode
    case 'ADD_SALARY_CODE_AMOUNT':
      return translations.effectAddSalaryCodeAmount
    default:
      return type
  }
}

export function getFieldDisplayName(
  key: string,
  translations: RuleTranslations
): string {
  switch (key) {
    case 'salaryWorkTime':
      return translations.salaryWorkTime
    case 'salaryCode':
      return translations.salaryCode
    case 'amount':
      return translations.amount
    default:
      return key
  }
}

export function partSummary(
  part: CompanyRulePartForm,
  translations: RuleTranslations
): string {
  const name = getRulePartDisplayName(part.type, translations)
  const values = Object.entries(part.values)
    .map(([key, value]) => part.labels[key] || String(value ?? ''))
    .filter(Boolean)

  if (values.length === 0) return name
  return `${name}: ${values.join(', ')}`
}

export function companyRuleToForm(rule: CompanyRuleNode): CompanyRuleForm {
  return {
    id: rule.id,
    name: rule.name,
    active: rule.active,
    trigger: {
      id: `${rule.id}-trigger`,
      type: rule.trigger,
      values: {},
      labels: {},
    },
    conditions: rule.conditions.map(condition => ({
      id: condition.id,
      type: condition.conditionType,
      values: {
        salaryWorkTime: condition.salaryWorkTime?.id ?? null,
        salaryCode: condition.salaryCode?.id ?? null,
      },
      labels: {
        salaryWorkTime: condition.salaryWorkTime?.name ?? '',
        salaryCode: formatSalaryCodeName(condition.salaryCode),
      },
    })),
    effects: rule.effects.map(effect => ({
      id: effect.id,
      type: effect.effectType,
      values: {
        salaryCode: effect.salaryCode?.id ?? null,
        amount: Number(effect.amount ?? 0),
      },
      labels: {
        salaryCode: formatSalaryCodeName(effect.salaryCode),
        amount: String(effect.amount ?? ''),
      },
    })),
  }
}

export function formToMutationInput(form: CompanyRuleForm): Record<string, unknown> {
  return {
    name: form.name,
    trigger:
      form.trigger?.type ??
      CompanyRuleTrigger.MANUAL_TIME_ENTRY_SALARY_CALCULATION,
    active: form.active,
    conditionsManual: form.conditions.map(part => {
      if (part.type === CompanyRuleConditionType.SALARY_WORK_TIME_EQUALS) {
        return {
          conditionType: part.type,
          operator: 'EQUALS',
          salaryWorkTime: part.values.salaryWorkTime,
        }
      }
      if (part.type === CompanyRuleConditionType.SALARY_CODE_EQUALS) {
        return {
          conditionType: part.type,
          operator: 'EQUALS',
          salaryCode: part.values.salaryCode,
        }
      }
      return {
        conditionType: part.type,
        operator: 'EQUALS',
      }
    }),
    effectsManual: form.effects.map(part => {
      if (part.type === CompanyRuleEffectType.ADD_SALARY_CODE_AMOUNT) {
        return {
          effectType: part.type,
          salaryCode: part.values.salaryCode,
          amount: part.values.amount,
        }
      }
      return {
        effectType: part.type,
      }
    }),
  }
}

export function isRuleValid(
  form: CompanyRuleForm,
  capabilities: CompanyRuleTriggerCapability[]
): boolean {
  const selectedCapability = capabilities.find(
    capability => capability.trigger === form.trigger?.type
  )
  if (!selectedCapability) return false
  if (!form.name) return false
  if (form.conditions.length === 0 || form.effects.length === 0) return false

  return (
    form.conditions.every(part =>
      isPartValid(part, selectedCapability.conditions)
    ) &&
    form.effects.every(part => isPartValid(part, selectedCapability.effects))
  )
}
